import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApplicationDetail } from './cer/ApplicationDetail';
import { ApplicationsQueue } from './cer/ApplicationsQueue';
import { ParticipantDetail } from './cer/ParticipantDetail';
import { ParticipantsPage } from './cer/ParticipantsPage';
import { RulesPage } from './cer/RulesPage';
import { IncidentDetailPage, IncidentsPage } from '../components/IncidentPages';
import { NewRecallPage, RecallDetailPage, RecallsPage } from '../components/RecallPages';
import { DeadLetterPage, ExchangePage, InboxMessagePage, OutboxMessagePage, ReconciliationRunPage } from '../components/ExchangePages';
import { LakeExportPage } from '../components/LakeExportPage';
import { MonitoringJobPage, MonitoringPage, MonitoringRunPage } from '../components/MonitoringPages';
import { RiskPage } from '../components/RiskPage';
import { ReportPage } from '../components/ReportPage';
import { AuditTrailPage } from '../components/AuditTrailPage';
import { EmailTestPage } from '../components/EmailTestPage';
import { TestingPage } from '../components/TestingPage';
import { HelpPage } from '../components/HelpPage';
import { UserAdminDetailPage, UserAdminListPage } from '../components/UserAdminPages';
import { ObligationsPage } from '../components/ObligationsPage';
import { CaseDetailPage, CasesPage, NewCasePage } from '../components/CasePages';
import { FollowUpsPage } from '../components/AuditPanel';
import { InstallationDetailPage, ModelDetailPage, NewInstallationPage, NotGeocodedPage, NewModelPage, NewUnitPage, RegisterPage, UnitDetailPage } from '../components/DevicePages';

const register = { mode: 'cer' as const, basePath: '/cer/register' };
const incidents = { mode: 'cer' as const, basePath: '/cer/incidents', devicesPath: '/cer/register' };
const recalls = { mode: 'cer' as const, basePath: '/cer/recalls', devicesPath: '/cer/register', incidentsPath: '/cer/incidents' };

export function CerWorkbench() {
  return (
    <Routes>
      <Route index element={<CerHome />} />
      <Route path="participants" element={<ParticipantsPage />} />
      <Route path="participants/applications" element={<ApplicationsQueue />} />
      <Route path="participants/applications/:id" element={<ApplicationDetail />} />
      <Route path="participants/:id" element={<ParticipantDetail />} />
      <Route path="register" element={<RegisterPage {...register} />} />
      <Route path="register/rules" element={<RulesPage />} />
      <Route path="register/models/new" element={<NewModelPage {...register} />} />
      <Route path="register/models/:id" element={<ModelDetailPage {...register} />} />
      <Route path="register/units/new" element={<NewUnitPage {...register} />} />
      <Route path="register/units/:id" element={<UnitDetailPage {...register} />} />
      <Route path="register/installations/new" element={<NewInstallationPage {...register} />} />
      <Route path="register/installations/not-geocoded" element={<NotGeocodedPage {...register} />} />
      <Route path="register/installations/:id" element={<InstallationDetailPage {...register} />} />
      <Route path="incidents" element={<IncidentsPage {...incidents} />} />
      <Route path="incidents/:id" element={<IncidentDetailPage {...incidents} />} />
      <Route path="recalls" element={<RecallsPage {...recalls} />} />
      <Route path="recalls/new" element={<NewRecallPage {...recalls} />} />
      <Route path="recalls/:id" element={<RecallDetailPage {...recalls} />} />
      <Route path="exchange" element={<ExchangePage />} />
      <Route path="exchange/lake" element={<LakeExportPage />} />
      <Route path="exchange/outbox/:id" element={<OutboxMessagePage />} />
      <Route path="exchange/inbox/:id" element={<InboxMessagePage />} />
      <Route path="exchange/dead-letters/:id" element={<DeadLetterPage />} />
      <Route path="exchange/reconciliation/:id" element={<ReconciliationRunPage />} />
      <Route path="compliance" element={<Navigate to="monitoring" replace />} />
      <Route path="compliance/monitoring" element={<MonitoringPage />} />
      <Route path="compliance/monitoring/runs/:id" element={<MonitoringRunPage />} />
      <Route path="compliance/monitoring/jobs/:id" element={<MonitoringJobPage />} />
      <Route path="compliance/obligations" element={<ObligationsPage />} />
      <Route path="compliance/risk" element={<RiskPage />} />
      <Route path="compliance/cases" element={<CasesPage mode="cer" basePath="/cer/compliance/cases" />} />
      <Route path="compliance/cases/new" element={<NewCasePage />} />
      <Route path="compliance/cases/:id" element={<CaseDetailPage mode="cer" basePath="/cer/compliance/cases" />} />
      <Route path="compliance/follow-ups" element={<FollowUpsPage />} />
      <Route path="compliance/report" element={<RedirectKeepingSearch to="/cer/reports" />} />
      <Route path="reports" element={<ReportPage />} />
      <Route path="audit" element={<AuditTrailPage />} />
      <Route path="users" element={<UserAdminListPage />} />
      <Route path="users/:id" element={<UserAdminDetailPage />} />
      <Route path="email-test" element={<EmailTestPage />} />
      <Route path="testing" element={<TestingPage />} />
      <Route path="help" element={<HelpPage />} />
      <Route path="*" element={<Placeholder />} />
    </Routes>
  );
}

function RedirectKeepingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

function CerHome() {
  const { user } = useAuth();
  return (
    <>
      <h1>CER workbench</h1>
      <p>Signed in as {user?.displayName}.</p>
      <div className="cards">
        <section className="card">
          <h2>Participants</h2>
          <p>Assess applications, record decisions with reasons, search and correct the participant register.</p>
          <p>
            <Link to="/cer/participants/applications">Applications queue</Link> · <Link to="/cer/participants">Participant register</Link>
          </p>
        </section>
        <section className="card">
          <h2>Device register</h2>
          <p>Register models, units and installations against approved participants; search the current state; review rejected submissions.</p>
          <p>
            <Link to="/cer/register">Device register</Link> · <Link to="/cer/register?tab=rejections">Rejected submissions</Link> · <Link to="/cer/register/rules">Rule sets</Link> · <Link to="/cer/register/installations/not-geocoded">Not geocoded</Link>
          </p>
          <p className="muted">Each record's detail page has a collapsed History with as-at reconstruction and corrections.</p>
        </section>
        <section className="card">
          <h2>Incidents & recalls</h2>
          <p>Fault reports submitted by participants against models, units and installations; acknowledge, investigate and resolve them. Raise recalls against a model or batch, track every affected unit to resolution and withdraw the model where needed.</p>
          <p>
            <Link to="/cer/incidents">Incidents</Link> · <Link to="/cer/incidents?status=open">Open incidents</Link> · <Link to="/cer/recalls">Recalls</Link> · <Link to="/cer/recalls/new">Raise a recall</Link>
          </p>
        </section>
        <section className="card">
          <h2>Information exchange</h2>
          <p>Versioned AEMO, STC and network interfaces; outbox and inbox logs, dead-letter replay and partner reconciliation for review; business-event export to the data lake.</p>
          <p>
            <Link to="/cer/exchange">Interface catalogue</Link> · <Link to="/cer/exchange?tab=dead-letters">Dead letters</Link> · <Link to="/cer/exchange?tab=reconciliation">Reconciliation</Link> · <Link to="/cer/exchange/lake">Data lake export</Link>
          </p>
        </section>
        <section className="card">
          <h2>Compliance</h2>
          <p>Publish effective-dated obligations and run effective-dated monitoring rules over the register as at any date; every run and hit is retained with the history version it matched, so results can be compared and reproduced. The risk view combines monitoring hits, open incidents, outstanding recall units, partner reconciliation differences and denied access by participant; open a case from a participant's risk row, schedule and record inspections and audits on it, record audit findings against the obligation version in force, track audit follow-ups in the follow-up register, and track it to closure on an append-only timeline.</p>
          <p>
            <Link to="/cer/compliance/monitoring">Monitoring rules &amp; runs</Link> · <Link to="/cer/compliance/obligations">Obligations</Link> · <Link to="/cer/compliance/risk">Risk view</Link> · <Link to="/cer/compliance/cases">Cases</Link> · <Link to="/cer/compliance/follow-ups">Follow-up register</Link> · <Link to="/cer/reports">Activity report</Link>
          </p>
        </section>
        <section className="card">
          <h2>Audit trail</h2>
          <p>Every recorded action, filtered by record, actor, action and date; a case shows its inspections, audits and enforcement actions too.</p>
          <p>
            <Link to="/cer/audit">Audit trail</Link>
          </p>
        </section>
        <section className="card">
          <h2>Reports</h2>
          <p>Compliance activity for a period by day, participant and activity, drilling to the records, reconciled to the audit trail and exported as CSV.</p>
          <p>
            <Link to="/cer/reports">Compliance activity report</Link>
          </p>
        </section>
      </div>
    </>
  );
}

function Placeholder() {
  return (
    <>
      <h1>Coming in a later phase</h1>
      <p className="muted">
        This area is planned for a later delivery phase. <Link to="/cer">Back to overview</Link>.
      </p>
    </>
  );
}
